import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import RecordingTemplateRoom from './RecordingTemplateRoom';
import './recordingTemplate.css';

/** Catches render errors here so GlobalErrorBoundary does not hide the real message in production. */
class RecordingTemplateErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[RecordingTemplate]', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div className="recording-template-help" role="alert">
          <h1>Recording template error</h1>
          <p>
            The egress layout page crashed. Fix the error below, redeploy the UI, then retry room
            composite.
          </p>
          <pre className="recording-template-help__pre">{error?.message || String(error)}</pre>
          {error?.stack ? (
            <details>
              <summary>Stack trace</summary>
              <pre className="recording-template-help__pre">{error.stack}</pre>
            </details>
          ) : null}
          <p>
            <Link to="/livekit">Back to LiveKit join</Link>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const DEFAULT_LOCAL_TEMPLATE =
  typeof window !== 'undefined' && window.location?.origin
    ? `${window.location.origin}/livekit/record-template`
    : 'http://localhost:3000/livekit/record-template';

/**
 * LiveKit Room Composite loads this URL in headless Chrome with ?url=&token=&layout=
 * (see https://docs.livekit.io/transport/media/ingress-egress/egress/custom-template.md).
 * For local dev, set LIVEKIT_RECORDING_TEMPLATE_URL on the token server to this page’s absolute URL.
 */
export default function LiveKitRecordingTemplatePage() {
  const [searchParams] = useSearchParams();
  const url = searchParams.get('url') || '';
  const token = searchParams.get('token') || '';
  const layout = searchParams.get('layout') || 'grid';

  if (!url || !token) {
    return (
      <div className="recording-template-help">
        <h1>Recording template (egress)</h1>
        <p>
          This route is opened by <strong>LiveKit Room Composite</strong> with{' '}
          <code>?url=&amp;token=&amp;layout=</code> query parameters. Open it from the egress worker, or
          use <code>lk egress test-template</code> against this URL.
        </p>
        <p>
          <strong>Local server env</strong> (video-streaming-server): set{' '}
          <code>LIVEKIT_RECORDING_TEMPLATE_URL</code> to something like:
        </p>
        <pre>{DEFAULT_LOCAL_TEMPLATE}</pre>
        <p className="recording-template-help__note">
          Note: LiveKit <strong>Cloud</strong> egress cannot reach <code>localhost</code>. Use this URL
          when running egress on the same machine / Docker network, or expose the UI via a tunnel (ngrok)
          and set the env to that HTTPS URL until you deploy.
        </p>
        <p>
          <Link to="/livekit">Back to LiveKit join</Link>
        </p>
      </div>
    );
  }

  return (
    <RecordingTemplateErrorBoundary>
      <RecordingTemplateRoom serverUrl={url} token={token} layout={layout} />
    </RecordingTemplateErrorBoundary>
  );
}
